import { useLayoutEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license-pro';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { SnackbarProvider } from 'notistack';

import { queryClient, queryPersistOptions } from '@/adapters/query';

import { ErrorBoundaryFallback } from './components/ErrorBoundaryFallback';
import { isPublicESignRoute } from './lib/isPublicSignerRoute';
import { loadGoogleMaps } from './lib/loadGoogleMaps';
import { loadUpscope } from './lib/loadUpscope';
import { Pylon } from './lib/Pylon';
import { dismissInitialLoadingScreen } from './utils/dismissInitialLoadingScreen';

import { AppRoutes } from './AppRoutes';
import theme from './theme';

import './styles.css';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_PRO_LICENSE_KEY);

// Skip third-party JS / support / co-browse / chat tooling on the public
// eSign token routes. The URL carries a bearer token; Google Maps Places,
// Upscope, and Pylon all execute third-party JS or capture URL/DOM context
// that must not run on a PHI / signature page.
if (!isPublicESignRoute()) {
  loadGoogleMaps();
  loadUpscope();
  new Pylon().load();
}

const App = () => {
  useLayoutEffect(() => {
    dismissInitialLoadingScreen();
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Sentry.ErrorBoundary
          showDialog
          fallback={({ error, resetError }) => (
            <ErrorBoundaryFallback
              error={error as Error}
              resetError={resetError}
            />
          )}
        >
          <BrowserRouter>
            <PersistQueryClientProvider
              client={queryClient}
              persistOptions={queryPersistOptions}
            >
              <SnackbarProvider maxSnack={3} autoHideDuration={6000}>
                <AppRoutes />
              </SnackbarProvider>
              <ReactQueryDevtools />
            </PersistQueryClientProvider>
          </BrowserRouter>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default App;
